import { useMemo, useSyncExternalStore } from 'react'

/**
 * The Guide panel's root element while a tour runs, or null.
 *
 * Kept outside React context so any dialog or drawer can read it wherever it
 * sits in the tree (the confirm dialog's provider wraps the tour provider),
 * and so a dialog re-renders only when the panel mounts or unmounts, never on
 * other tour state.
 */
let panelNode = null
const listeners = new Set()

/** Ref callback for the panel root: the element on mount, null on unmount. */
export function setGuidePanelNode(node) {
  const next = node ?? null
  if (next === panelNode) return
  panelNode = next
  for (const listener of [...listeners]) listener()
}

function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function snapshot() {
  return panelNode
}

function serverSnapshot() {
  return null
}

/** The mounted Guide panel root, or null when no tour panel is on the page. */
export function useGuidePanelNode() {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot)
}

/**
 * The Guide panel as a CFocusTrap `additionalContainer`: while a tour panel
 * is on the page, Tab flows between a dialog or drawer and the panel, and
 * focus moved into the panel is never pulled back. A new identity per panel
 * node re-subscribes the trap when the panel mounts or unmounts.
 */
export function useGuidePanelContainer() {
  const panelNode = useGuidePanelNode()
  return useMemo(() => ({ current: panelNode }), [panelNode])
}
